/**
 * One sheet at a time (#703). Every sheet is a parameter in the address (`?person=`, `?order=`, …), so two could be
 * open at once and each had to be closed on its own. A step that opens a sheet on the page takes every other sheet's
 * parameter out, and when a sheet was already open it replaces that entry instead of adding one: whatever opened
 * it (a row, a link, ⌘K, a menu), the page has one sheet, and Back or Close leaves it with none.
 */
import { useEffect, useRef } from "react";
import { NavigationType, useBlocker, useNavigate, type Location } from "react-router-dom";

/** The parameters that name an open sheet, on every screen. */
export const SHEET_PARAMS = ["person", "order", "panel", "run", "open", "report", "campaign"] as const;
type SheetParam = (typeof SHEET_PARAMS)[number];

/**
 * A sheet that opens over another and closes back to it: a run of Settings' Sync activity (`?open=activity&run=…`,
 * #716), and a person opened from Data quality's possible duplicates (`?open=quality&person=…`, #731). While the child
 * is open the parent's parameter stays in the address (its host keeps the parent's sheet shut), and opening the child
 * from the parent adds an entry, so Back or Close returns to the parent.
 */
const NESTED: Partial<Record<SheetParam, readonly [parent: SheetParam, value: string]>> = { run: ["open", "activity"], person: ["open", "quality"] };

/** The parent a sheet that is opening keeps, when the address holds it. */
const parentOf = (key: SheetParam, then: URLSearchParams): SheetParam | null => {
  const nested = NESTED[key];
  return nested && then.get(nested[0]) === nested[1] ? nested[0] : null;
};

/**
 * A sheet entry's history state. `sheet`: the entry was added to open the sheet, so closing it is a step back (to
 * the page without it). `settled`: the step has been through `sheetStep` already.
 */
export interface SheetState {
  sheet?: boolean;
  settled?: boolean;
}

/** True when the entry was added to open its sheet: closing it goes back one step. */
export const pushedForSheet = (state: unknown): boolean => !!(state as SheetState | null)?.sheet;

/** What a step on the same page becomes: nothing at all, a step back, or a navigation made again as said. */
export type SheetStep = "none" | "back" | { search: string; replace: boolean; state: SheetState };

const sorted = (params: URLSearchParams) => [...params].sort(([a, b], [c, d]) => a.localeCompare(c) || b.localeCompare(d)).join("&");

/**
 * How a step on the same page should land, or null when it is left alone. The rules, while a sheet is open or opens:
 * 1. A step to the address already open (its own row or link pressed again, a double-click) is nothing: no entry.
 * 2. A step that opens a sheet takes every other sheet's parameter out. Over an open sheet it replaces that sheet's
 *    entry and keeps what it was (one added for a sheet stays one step from the page); from none, it adds one.
 * 3. A change to the list under an open sheet (a sort, a filter, a page) takes the place of the sheet's entry, which
 *    is then the list's own: closing the sheet takes its parameter out in place and the change stays, where a step
 *    back would undo the change with the sheet. Nothing is left behind for Back to reopen.
 * 4. So does a change to the list that takes the sheet away with it (another view, a filter link that names no
 *    sheet): the sheet's entry becomes the new list's, and Back goes to the page before the sheet, never to it.
 * 5. A step that only closes the sheet (its parameter out, the list as it was: the sidebar's entry for this page) is
 *    a close: a step back over the entry the sheet added, else its parameter out in place. Pushed, it would leave the
 *    sheet's entry behind for Back to reopen. (The hosts' own close is a step back or a replace: never held.)
 * A step to another page is left alone.
 */
/** A pathname as the page it names: `/events/ce21%3A1` and `/events/ce21:1` are one page. */
function samePage(a: string, b: string): boolean {
  const decoded = (path: string) => {
    try {
      return decodeURI(path).replace(/%3A/gi, ":");
    } catch {
      return path;
    }
  };
  return a === b || decoded(a) === decoded(b);
}

export function sheetStep(current: Pick<Location, "pathname" | "search" | "state">, next: Pick<Location, "pathname" | "search">, push = true): SheetStep | null {
  if (!samePage(current.pathname, next.pathname)) return null;
  const now = new URLSearchParams(current.search);
  const then = new URLSearchParams(next.search);
  const open = SHEET_PARAMS.some((key) => now.has(key));
  if (sorted(now) === sorted(then)) return open || push ? "none" : null;
  const opened = SHEET_PARAMS.filter((key) => then.has(key) && then.get(key) !== now.get(key));
  if (opened.length > 0) {
    const parents = opened.flatMap((key) => parentOf(key, then) ?? []);
    for (const key of SHEET_PARAMS) if (!opened.includes(key) && !parents.includes(key)) then.delete(key);
    // A child opened from its parent (not one child in another's place) is a step of its own: Back returns to the parent.
    if (parents.length > 0 && opened.every((key) => !now.has(key))) return { search: then.toString(), replace: false, state: { sheet: true, settled: true } };
    return { search: then.toString(), replace: open, state: { sheet: open ? pushedForSheet(current.state) : true, settled: true } };
  }
  if (!open) return null;
  const kept = SHEET_PARAMS.some((key) => now.has(key) && then.get(key) === now.get(key));
  const page = (params: URLSearchParams) => sorted(new URLSearchParams([...params].filter(([key]) => !(SHEET_PARAMS as readonly string[]).includes(key))));
  const onlyCloses = !kept && page(now) === page(then);
  if (onlyCloses) return push ? (pushedForSheet(current.state) ? "back" : { search: then.toString(), replace: true, state: { settled: true } }) : null;
  return kept || push ? { search: then.toString(), replace: true, state: { settled: true } } : null;
}

/**
 * Holds each step on the page and makes it again as `sheetStep` says. Once, inside the (data) router. The step is
 * worked out inside the blocker, from the pair of locations the router hands it, and made again by its query alone
 * (a navigation with no pathname stays on the page it is on), so it never depends on how a path is spelled. A step
 * back or forward is never held.
 */
export function useOneSheet(): void {
  const navigate = useNavigate();
  const held = useRef<{ step: SheetStep; hash: string } | null>(null);
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    if (historyAction === NavigationType.Pop || (nextLocation.state as SheetState | null)?.settled) return false;
    const step = sheetStep(currentLocation, nextLocation, historyAction === NavigationType.Push);
    held.current = step && { step, hash: nextLocation.hash };
    return step !== null;
  });
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const now = held.current;
    held.current = null;
    blocker.reset();
    if (!now || now.step === "none") return;
    if (now.step === "back") void navigate(-1);
    else void navigate({ search: now.step.search, hash: now.hash }, { replace: now.step.replace, state: now.step.state, preventScrollReset: true });
  }, [blocker, navigate]);
}

/** `useOneSheet` as an element, for the route that holds the shell. */
export function OneSheet(): null {
  useOneSheet();
  return null;
}
